import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateAdminAccountMembership } from 'api/adminAccounts';
import { queryKeys } from 'constants/queryKeys';
import { accountManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import { ME_QUERY_KEY } from 'hooks/useMe';
import type { TUpdateAdminAccountMembershipRequest } from 'types';

type UseUpdateAdminAccountMembershipOptions = {
  onSuccess?: () => void;
};

type UpdateMembershipParams = {
  accountId: number;
  organizationId: number;
  request: TUpdateAdminAccountMembershipRequest;
};

/** 所属の状態変更・追加。冪等なので「追加」も同じ API を ACTIVE で呼ぶ。 */
export function useUpdateAdminAccountMembership(
  options: UseUpdateAdminAccountMembershipOptions = {},
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      accountId,
      organizationId,
      request,
    }: UpdateMembershipParams) => {
      if (!user) {
        throw new Error(accountManagementStrings.edit.errors.unauthorized);
      }
      return updateAdminAccountMembership(accountId, organizationId, request);
    },
    onSuccess: async (_account, variables) => {
      if (!user) return;

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [queryKeys.ADMIN_ACCOUNTS, user.uid],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            queryKeys.ADMIN_ACCOUNT_DETAIL,
            variables.accountId,
            user.uid,
          ],
        }),
        // 自分の所属を操作した場合、切り替えメニューと未読サマリーも更新する
        queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY }),
        // 所属の増減で利用規約の同意要否が変わり得る
        queryClient.invalidateQueries({
          queryKey: [queryKeys.ACCOUNT_TERMS, variables.accountId],
        }),
        queryClient.invalidateQueries({
          queryKey: [queryKeys.TERMS_MEMBER_STATUSES],
        }),
      ]);

      options.onSuccess?.();
    },
  });

  const updateAsync = async (params: UpdateMembershipParams) => {
    await mutation.mutateAsync(params);
  };

  return {
    updateAsync,
    isSubmitting: mutation.isPending,
  };
}
